import {useEffect, useCallback, useState} from 'react'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {createGroupPermission, getCategoryPermissions} from '../../core/_requests'
import {ICategory, TGroupRequestCreate} from '../../../core/group'
import {KTIcon} from '../../../../../_metronic/helpers'
import {GroupsSchema} from '../../../core/GroupsVali'
import {Placeholder} from 'react-bootstrap'

const SkeletonTable = () => {
  const rows = Array.from({length: 6})
  const cols = Array.from({length: 4})

  return (
    <table className='table align-middle table-row-dashed fs-6 gy-5'>
      <tbody>
        {rows.map((_, rowIndex) => (
          <tr key={rowIndex}>
            {cols.map((_, colIndex) => (
              <td key={colIndex}>
                <Placeholder as='p' animation='glow'>
                  <Placeholder xs={12} />
                </Placeholder>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const treatCategoriesPermissions = (categoriesPermissions: ICategory[]): ICategory[] => {
  if (!Array.isArray(categoriesPermissions)) {
    return []
  }
  return categoriesPermissions.map((category: any) => ({
    id: category.id,
    name: category.name,
    i18n: category.i18n,
    permissions: category.permissions
      .map((permission) => ({
        id: permission.id,
        name: permission.name,
        i18n: permission.i18n,
        isChecked: false,
      }))
      .filter((p) => !p.name.includes('index')),
  }))
}
const treatToSend = (categories: ICategory[]) => {
  const allPermissionsArray = categories.flatMap((category) => category.permissions)
  return allPermissionsArray.filter((p) => p.isChecked).map((permission) => permission.id)
}

const initialValue: ICategory[] = []

export const CreateModalGroup = ({close}: TGroupRequestCreate) => {
  const [loading, setLoading] = useState(true)

  const getPermissions = useCallback(async () => {
    try {
      setLoading(true)
      const categoryPermissions = await getCategoryPermissions()
      const treatCategoryPermissions = treatCategoriesPermissions(categoryPermissions)
      formik.setValues({name: '', level: 0, items: treatCategoryPermissions})
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response.data.message,
        timer: 2000,
      })
      close()
    } finally {
      setLoading(false)
    }
  }, [])

  const createGroupPermissions = async (
    groupName: string,
    level: number,
    permissions: number[]
  ) => {
    const ObjectRequest = {
      name: groupName,
      level: level,
      permissions: permissions,
    }

    Swal.fire({
      title: 'Deseja salvar o Grupo ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          await createGroupPermission(ObjectRequest)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })
          window.location.reload()
        } catch (error: any) {
          if (error.response.status === 422) {
            let errorText = ''
            for (let key in error.response.data.errors) {
              errorText += '<li>' + error.response.data.errors[key][0] + '</li>'
            }
            Swal.fire({
              icon: 'error',
              title: 'Ops! Revise seu formulário e tente novamente!',
              html: errorText,
            })
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: error.response.data.message,
              timer: 2000,
            })
          }
        } finally {
          setLoading(false)
        }
      }
    })
  }

  useEffect(() => {
    getPermissions()
  }, [getPermissions])

  const formik = useFormik({
    initialValues: {name: '', level: 0, items: initialValue},
    validationSchema: GroupsSchema,
    onSubmit: async (values) => {
      try {
        const request = treatToSend(values.items)
        await createGroupPermissions(values.name, values.level, request)
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error.response.data.message,
          timer: 2000,
        })
      }
    },
  })

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_edit_group'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-750px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Adicionar Grupo</h2>
              <span
                role='button'
                onClick={() => close()}
                className='btn btn-icon btn-sm btn-active-icon-primary'
              >
                <KTIcon iconName='cross' className='fs-1' />
              </span>
            </div>
            <div className='modal-body scroll-y mx-5 my-7'>
              <form id='kt_modal_update_role_form' className='form' onSubmit={formik.handleSubmit}>
                <div className='d-flex flex-column'>
                  <div className='fv-row mb-5'>
                    <label className='fs-5 fw-bolder form-label mb-2 w-100'>Nome do Grupo</label>
                    {!loading && (
                      <input
                        type='text'
                        className='form-control'
                        {...formik.getFieldProps('name')}
                        onChange={(e) => {
                          formik.setFieldValue(`name`, e.target.value)
                        }}
                      />
                    )}
                    {loading && (
                      <Placeholder as='p' animation='glow'>
                        <Placeholder xs={12} />
                      </Placeholder>
                    )}
                    {formik.errors.name && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.name}</div>
                      </div>
                    )}
                  </div>
                  <div className='fv-row mb-5'>
                    <label className='fs-5 fw-bolder form-label mb-2 w-100'>Nível de acesso</label>
                    {!loading && (
                      <input
                        type='number'
                        className='form-control'
                        {...formik.getFieldProps('level')}
                        min={0}
                        max={998}
                        step={1}
                        placeholder='Nível mínimo 0 e máximo 998'
                        onChange={(e) => {
                          formik.setFieldValue(`level`, e.target.value)
                        }}
                      />
                    )}
                    {loading && (
                      <Placeholder as='p' animation='glow'>
                        <Placeholder xs={12} />
                      </Placeholder>
                    )}
                    {formik.errors.level && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.level}</div>
                      </div>
                    )}
                  </div>
                  <div className='fv-row'>
                    <label className='fs-5 fw-bolder form-label mb-2'>Permissões Atuais</label>
                    {typeof formik.errors.items === 'string' && (
                      <div className='fv-plugins-message-container mb-2'>
                        <div className='fv-help-block'>{formik.errors.items}</div>
                      </div>
                    )}
                    {!loading && (
                      <div className='container'>
                        {formik.values.items.map((category, iCategory) => (
                          <div className='mb-4' key={category.id}>
                            <h5 className='text-gray-800'>{category.i18n}</h5>
                            <div className='row'>
                              {category.permissions.map((permission, iPermission) => (
                                <div className='col-6 col-md-4 mb-2' key={permission.id}>
                                  <div className='form-check d-flex align-items-start'>
                                    <input
                                      className='form-check-input me-2'
                                      type='checkbox'
                                      checked={Boolean(permission?.isChecked)}
                                      id={permission.id.toString()}
                                      name={permission.id.toString()}
                                      onChange={() => {
                                        formik.setFieldValue(
                                          `items[${iCategory}].permissions[${iPermission}].isChecked`,
                                          !permission?.isChecked
                                        )
                                      }}
                                    />
                                    <label
                                      className='form-check-label'
                                      htmlFor={permission.id.toString()}
                                    >
                                      {permission.i18n}
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {loading && <SkeletonTable />}
                  </div>
                </div>
                <div className='text-center pt-15'>
                  <button
                    type='reset'
                    onClick={() => close()}
                    className='btn btn-light me-3'
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    Fechar
                  </button>
                  <button
                    type='submit'
                    className='btn btn-danger'
                    disabled={loading || !formik.isValid}
                  >
                    <span className='indicator-label'>Salvar</span>
                    <span className='indicator-progress'>
                      Aguarde...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}
